
import React, { useEffect, useState } from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getCategories, Category } from "@/utils/categories";

interface CategorySelectorProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

const CategorySelector = ({ value, onChange, error }: CategorySelectorProps) => {
  const [categories, setCategories] = useState<Category[]>([]);
  
  useEffect(() => {
    // Charger les catégories au montage du composant
    const allCategories = getCategories();
    console.log("Catégories chargées dans CategorySelector:", allCategories);
    // Filter out the "Tous" (blank ID) category which is only used in the filter
    const selectableCategories = allCategories.filter(category => category.id !== "");
    setCategories(selectableCategories);
    
    // Mettre à jour les catégories si elles changent dans le localStorage
    const handleStorageChange = () => {
      const updatedCategories = getCategories().filter(category => category.id !== "");
      setCategories(updatedCategories);
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Catégorie
      </label>
      <ToggleGroup 
        type="single" 
        value={value}
        onValueChange={onChange}
        className="flex flex-wrap gap-2"
      >
        {categories.map(({ id, label, icon: Icon, description, imageUrl }) => (
          <div key={id} className="flex items-center group">
            <ToggleGroupItem 
              value={id} 
              className={`flex items-center justify-center gap-2 transition-colors duration-200 relative overflow-hidden ${
                value === id 
                  ? "bg-primary text-white hover:bg-primary-dark" 
                  : "hover:bg-primary/10"
              }`}
            >
              {imageUrl && (
                <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity">
                  <img src={imageUrl} alt={label} className="w-full h-full object-cover" />
                </div>
              )}
              <Icon className="h-4 w-4 relative z-10" />
              <span className="relative z-10">{label}</span>
            </ToggleGroupItem>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="ml-1">
                  <HelpCircle className="h-3 w-3 text-gray-500" />
                </TooltipTrigger>
                <TooltipContent className="bg-white p-2 shadow-lg rounded-md border max-w-xs break-words whitespace-normal">
                  <p>{description}</p>
                  {imageUrl && (
                    <div className="mt-2">
                      <img src={imageUrl} alt={label} className="w-full h-auto rounded-md" />
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ))}
      </ToggleGroup>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default CategorySelector;
